import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { DataGrid } from "@material-ui/data-grid";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  CircularProgress,
  Snackbar,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  LinearProgress,
} from "@material-ui/core";
import html2canvas from "html2canvas";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";

import { db, auth } from "../firebase";

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "")
  .split(",")
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean);

const TYPE_ROUTES = {
  wedding: "/wedding-invitation",
  "save-the-date": "/save-the-date",
  "bridal-shower": "/bridal-shower",
  "brit-milah": "/brit-milah",
};

const guessType = (row) => {
  if (row.category && TYPE_ROUTES[row.category]) return row.category;
  if (row.type && TYPE_ROUTES[row.type]) return row.type;
  const t = (row.template || "").toLowerCase();
  if (t.includes("baby") || t.includes("brit")) return "brit-milah";
  if (t.includes("shower")) return "bridal-shower";
  if (t.includes("save")) return "save-the-date";
  return "wedding";
};

const toDateInputValue = (val) => {
  if (!val) return "";
  let d;
  if (val && typeof val.toDate === "function") d = val.toDate();
  else d = new Date(val);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const formatExpiry = (val) => {
  if (!val) return "—";
  let d;
  if (val && typeof val.toDate === "function") d = val.toDate();
  else d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleString();
};

const expiryStatus = (val) => {
  if (!val) return "no date";
  let d;
  if (val && typeof val.toDate === "function") d = val.toDate();
  else d = new Date(val);
  if (isNaN(d.getTime())) return "invalid";
  const now = new Date();
  const diffDays = Math.round((d.getTime() - now.getTime()) / 86400000);
  if (diffDays < 0) return `expired (${-diffDays}d ago)`;
  if (diffDays === 0) return "today";
  return `${diffDays}d left`;
};

function LoginForm({ onError }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setLocalError("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      const msg = err.code || err.message || "Login failed";
      setLocalError(msg);
      if (onError) onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: 400,
        margin: "80px auto",
        padding: 32,
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="h4" align="center" gutterBottom>
        Admin Login
      </Typography>
      <Typography variant="body2" align="center" color="textSecondary" gutterBottom>
        Sign in to manage invitations
      </Typography>
      <form onSubmit={submit}>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          margin="normal"
          required
          autoFocus
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        {localError && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            {localError}
          </Typography>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={submitting}
          style={{ marginTop: 16 }}
        >
          {submitting ? "Signing in…" : "Sign In"}
        </Button>
      </form>
    </div>
  );
}

function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState("");
  const [exporting, setExporting] = useState(null);
  const [exportProgress, setExportProgress] = useState(0);
  const exportFrameRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  const isAdmin = (u) => {
    if (!u) return false;
    if (ADMIN_EMAILS.length === 0) return true;
    return ADMIN_EMAILS.includes((u.email || "").toLowerCase());
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setRows([]);
    } catch (e) {
      setSnack("Logout failed: " + (e.message || e));
    }
  };

  const fetchInvites = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(collection(db, "Invitations"));
      const list = snap.docs.map((d) => {
        const data = d.data() || {};
        return { id: d.id, ...data };
      });
      setRows(list);
    } catch (e) {
      setError(e.message || "Failed to load invitations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && isAdmin(user)) fetchInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const allFieldKeys = useMemo(() => {
    const keys = new Set();
    rows.forEach((r) =>
      Object.keys(r).forEach((k) => {
        if (k !== "id") keys.add(k);
      })
    );
    return Array.from(keys).sort();
  }, [rows]);

  const columns = useMemo(() => {
    const base = [
      { field: "id", headerName: "Document ID", width: 220 },
      {
        field: "isActive",
        headerName: "Active",
        width: 110,
        renderCell: (params) => (params.value ? "✅ Yes" : "❌ No"),
      },
      {
        field: "eventDate",
        headerName: "Event / Expires",
        width: 200,
        renderCell: (params) => formatExpiry(params.value),
      },
      {
        field: "_status",
        headerName: "Status",
        width: 150,
        valueGetter: (params) => expiryStatus(params.row.eventDate),
      },
    ];
    const seen = new Set(base.map((c) => c.field));
    const dynamic = allFieldKeys
      .filter((k) => !seen.has(k))
      .map((k) => ({
        field: k,
        headerName: k,
        width: 180,
        renderCell: (params) => {
          const v = params.value;
          if (v === undefined || v === null) return "";
          if (typeof v === "object") {
            if (typeof v.toDate === "function") return v.toDate().toLocaleString();
            try {
              return JSON.stringify(v);
            } catch (_) {
              return String(v);
            }
          }
          return String(v);
        },
      }));
    const actions = [
      {
        field: "_actions",
        headerName: "Actions",
        width: 320,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <div>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => openEdit(params.row)}
              style={{ marginRight: 6 }}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              disabled={!!exporting}
              onClick={() => exportAsVideo(params.row)}
            >
              {exporting === params.row.id
                ? `Recording ${exportProgress}%`
                : "Export Video"}
            </Button>
          </div>
        ),
      },
    ];
    return [...base, ...dynamic, ...actions];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFieldKeys, exporting, exportProgress]);

  const openEdit = (row) => {
    const initial = {};
    Object.keys(row).forEach((k) => {
      if (k === "id") return;
      const v = row[k];
      if (v && typeof v.toDate === "function") initial[k] = v.toDate().toISOString();
      else initial[k] = v;
    });
    setEditValues(initial);
    setEditing(row);
  };

  const closeEdit = () => {
    setEditing(null);
    setEditValues({});
  };

  const updateField = (key, value) => {
    setEditValues((prev) => ({ ...prev, [key]: value }));
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {};
      Object.keys(editValues).forEach((k) => {
        let v = editValues[k];
        if (k === "eventDate" && v) {
          const d = new Date(v);
          if (!isNaN(d.getTime())) v = Timestamp.fromDate(d);
        }
        payload[k] = v;
      });
      await updateDoc(doc(db, "Invitations", editing.id), payload);
      setSnack("Saved");
      closeEdit();
      fetchInvites();
    } catch (e) {
      setSnack("Save failed: " + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const exportAsVideo = async (row) => {
    if (exporting) return;
    setExporting(row.id);
    setExportProgress(0);
    const type = guessType(row);
    const url = `${TYPE_ROUTES[type]}?documentId=${encodeURIComponent(row.id)}`;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.left = "-10000px";
    iframe.style.top = "0";
    iframe.style.width = "900px";
    iframe.style.height = "1200px";
    iframe.style.border = "0";
    iframe.src = url;
    document.body.appendChild(iframe);
    exportFrameRef.current = iframe;

    const cleanup = () => {
      if (exportFrameRef.current && exportFrameRef.current.parentNode) {
        exportFrameRef.current.parentNode.removeChild(exportFrameRef.current);
      }
      exportFrameRef.current = null;
      setExporting(null);
      setExportProgress(0);
    };

    try {
      await new Promise((resolve) => {
        if (iframe.contentDocument && iframe.contentDocument.readyState === "complete") {
          resolve();
        } else {
          iframe.addEventListener("load", () => resolve(), { once: true });
        }
      });
      await new Promise((r) => setTimeout(r, 1500));

      const innerDoc = iframe.contentDocument;
      const target =
        innerDoc.getElementById("fullInvite") ||
        innerDoc.querySelector(".envContainer") ||
        innerDoc.body;

      const canvas = document.createElement("canvas");
      canvas.width = 900;
      canvas.height = 1200;
      const ctx = canvas.getContext("2d");

      if (typeof canvas.captureStream !== "function") {
        throw new Error("Browser does not support canvas.captureStream");
      }
      const stream = canvas.captureStream(15);

      let mimeType = "video/webm;codecs=vp9";
      if (!window.MediaRecorder || !MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "video/webm;codecs=vp8";
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "video/webm";

      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };

      const stopped = new Promise((resolve) => {
        recorder.onstop = () => resolve();
      });

      recorder.start();

      const totalMs = 6000;
      const start = performance.now();
      let stop = false;

      const drawLoop = async () => {
        while (!stop) {
          try {
            const frame = await html2canvas(target, {
              useCORS: true,
              backgroundColor: "#ffffff",
              logging: false,
              scale: 1,
              width: 900,
              height: 1200,
              windowWidth: 900,
              windowHeight: 1200,
            });
            ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
          } catch (err) {
            // skip frame on error
          }
          const elapsed = performance.now() - start;
          setExportProgress(Math.min(99, Math.round((elapsed / totalMs) * 100)));
          if (elapsed >= totalMs) {
            stop = true;
            break;
          }
          await new Promise((r) => setTimeout(r, 200));
        }
        recorder.stop();
      };

      drawLoop();
      await stopped;

      const blob = new Blob(chunks, { type: "video/webm" });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `invitation-${row.id}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
      setSnack("Video exported");
    } catch (e) {
      setSnack("Export failed: " + (e.message || e));
    } finally {
      cleanup();
    }
  };

  const expired = rows.filter((r) => {
    const v = r.eventDate;
    if (!v) return false;
    const d = v && typeof v.toDate === "function" ? v.toDate() : new Date(v);
    return !isNaN(d.getTime()) && d.getTime() < Date.now();
  }).length;
  const active = rows.filter((r) => r.isActive).length;

  if (!authReady) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <CircularProgress />
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  if (!isAdmin(user)) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Access denied
        </Typography>
        <Typography variant="body1" gutterBottom>
          {user.email} is not an authorized admin.
        </Typography>
        <Button onClick={handleLogout} variant="outlined" color="primary">
          Sign Out
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", minHeight: "100vh" }}>
      <Typography variant="h3" align="center" gutterBottom>
        Admin Dashboard — Invitations
      </Typography>
      <Typography variant="subtitle1" align="center" gutterBottom>
        Total: {rows.length} · Active: {active} · Expired: {expired}
      </Typography>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Signed in as <b>{user.email}</b>
        </Typography>
        <div>
          <Button
            onClick={fetchInvites}
            variant="outlined"
            color="primary"
            style={{ marginRight: 8 }}
          >
            Refresh
          </Button>
          <Button onClick={handleLogout} variant="outlined">
            Sign Out
          </Button>
        </div>
      </div>
      {exporting && (
        <div style={{ margin: "10px 0" }}>
          <Typography variant="caption">
            Recording invitation {exporting}…
          </Typography>
          <LinearProgress variant="determinate" value={exportProgress} />
        </div>
      )}
      {error && (
        <Typography color="error" align="center">
          {error}
        </Typography>
      )}
      <div style={{ height: "75vh", width: "100%", background: "#fff" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <CircularProgress />
          </div>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
          />
        )}
      </div>

      <Dialog open={!!editing} onClose={closeEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Invitation {editing && <small>({editing.id})</small>}
        </DialogTitle>
        <DialogContent dividers>
          <FormControlLabel
            control={
              <Switch
                checked={!!editValues.isActive}
                onChange={(e) => updateField("isActive", e.target.checked)}
                color="primary"
              />
            }
            label="Active"
          />
          <TextField
            label="Event Date / Expires"
            type="datetime-local"
            value={toDateInputValue(editValues.eventDate)}
            onChange={(e) => updateField("eventDate", e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          {editValues.category !== undefined || editValues.type !== undefined ? (
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                value={editValues.category || editValues.type || ""}
                onChange={(e) => updateField("category", e.target.value)}
              >
                {Object.keys(TYPE_ROUTES).map((k) => (
                  <MenuItem value={k} key={k}>
                    {k}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {Object.keys(editValues)
            .filter((k) => !["isActive", "eventDate", "category", "type"].includes(k))
            .map((k) => {
              const v = editValues[k];
              const isObj = v && typeof v === "object";
              return (
                <TextField
                  key={k}
                  label={k}
                  value={isObj ? JSON.stringify(v) : v == null ? "" : String(v)}
                  onChange={(e) => updateField(k, e.target.value)}
                  fullWidth
                  margin="dense"
                  multiline={typeof v === "string" && v.length > 60}
                />
              );
            })}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={saveEdit}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack("")}
        message={snack}
      />
    </div>
  );
}

export default AdminDashboard;
